import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Code, Tag, Search, Loader2, Copy, CheckCircle } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';

const TABS = [
  { id: 'analyze', label: 'Analyze Page' },
  { id: 'generate', label: 'Generate Tags' },
  { id: 'keywords', label: 'Local Keywords' },
  { id: 'image', label: 'Image Geotagging' },
];

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
  return err.message || 'Request failed';
};

function CopyBlock({ label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
        <button type="button" onClick={copy} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700">
          {copied ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-48 whitespace-pre-wrap border border-slate-200 dark:border-slate-800">
        {text}
      </pre>
    </div>
  );
}

export default function GeotaggingPage() {
  const { selectedProjectId } = useProject();
  const [activeTab, setActiveTab] = useState('analyze');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);

  // Shared fields
  const [url, setUrl] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('US');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [service, setService] = useState('');

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('Analyzing geo tags...');
    setResult(null);
    try {
      const { data } = await api.post('/geotagging/analyze', {
        url,
        city: city || undefined,
        state: state || undefined,
        country,
        latitude: lat ? parseFloat(lat) : undefined,
        longitude: lng ? parseFloat(lng) : undefined,
      });
      setResult(data);
      setStatus('Analysis complete.');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!lat || !lng || !city) {
      setStatus('Error: Latitude, longitude, and city are required.');
      return;
    }
    setLoading(true);
    setStatus('Generating geo meta tags...');
    setResult(null);
    try {
      const { data } = await api.post('/geotagging/generate-meta', {
        url: url || 'https://example.com',
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        city,
        state: state || undefined,
        country,
        project_id: selectedProjectId || undefined,
      });
      setResult(data);
      setStatus('Tags generated!');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeywords = async (e) => {
    e.preventDefault();
    if (!city) {
      setStatus('Error: City is required.');
      return;
    }
    setLoading(true);
    setStatus('Fetching keyword suggestions...');
    setResult(null);
    try {
      const { data } = await api.get('/geotagging/local-keyword-suggestions', {
        params: { city, service: service || undefined },
      });
      setResult(data);
      setStatus('');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImageGuide = async (e) => {
    e.preventDefault();
    if (!lat || !lng) {
      setStatus('Error: Latitude and longitude are required.');
      return;
    }
    setLoading(true);
    setStatus('Fetching image geotag guide...');
    setResult(null);
    try {
      const { data } = await api.post('/geotagging/image-geotag-guide', {
        image_urls: [],
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        city: city || undefined,
        country,
      });
      setResult(data);
      setStatus('');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (activeTab === 'analyze') return handleAnalyze(e);
    if (activeTab === 'generate') return handleGenerate(e);
    if (activeTab === 'keywords') return handleKeywords(e);
    return handleImageGuide(e);
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <MapPin className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Geotagging</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Optimize your pages and images with geo-metadata for local search visibility.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === t.id
                ? 'border-b-2 border-violet-600 text-violet-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => { setActiveTab(t.id); setResult(null); setStatus(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">

          {/* URL field for analyze/generate tabs */}
          {(activeTab === 'analyze' || activeTab === 'generate') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Page URL {activeTab === 'analyze' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yoursite.com/services"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required={activeTab === 'analyze'}
              />
            </div>
          )}

          {/* Coordinates */}
          {(activeTab === 'analyze' || activeTab === 'generate' || activeTab === 'image') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Latitude {activeTab !== 'analyze' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  step="any"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  placeholder="e.g. 40.7128"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required={activeTab !== 'analyze'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Longitude {activeTab !== 'analyze' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  step="any"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  placeholder="e.g. -74.0060"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required={activeTab !== 'analyze'}
                />
              </div>
            </div>
          )}

          {/* City / State */}
          {activeTab !== 'analyze' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="New York"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">State / Region</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="NY"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>
          )}

          {/* City-only for analyze */}
          {activeTab === 'analyze' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">City (optional)</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="New York"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Country</label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>
          )}

          {/* Service keyword for local keywords tab */}
          {activeTab === 'keywords' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Miami"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Service / Business Type</label>
                <input
                  type="text"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  placeholder="plumber"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? 'Processing...' : {
              analyze: 'Analyze Geo Tags',
              generate: 'Generate Meta Tags',
              keywords: 'Get Keyword Suggestions',
              image: 'Get Image Guide',
            }[activeTab]}
          </button>
        </form>

        {/* Status */}
        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
          }`}>
            {status}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="mt-6 space-y-5">
            {/* Analyze results */}
            {activeTab === 'analyze' && result.checks && (
              <>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-violet-600">{result.score}</p>
                    <p className="text-xs text-slate-500">Geo Score</p>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <p><strong className="text-red-500">{result.missing_count}</strong> geo signals missing</p>
                    <p className="text-xs text-slate-400">{result.url}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {result.checks.map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-2 rounded-md bg-slate-50 dark:bg-slate-900 text-sm">
                      <span className="text-slate-700 dark:text-slate-300">{c.label}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          c.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300' :
                          c.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                        }`}>{c.priority}</span>
                        <span className="text-xs text-red-500 font-medium capitalize">{c.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {result.recommendations?.length > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg space-y-1">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Recommendations</p>
                    {result.recommendations.map((r, i) => (
                      <p key={i} className="text-xs text-blue-700 dark:text-blue-400">• {r}</p>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Generate results */}
            {activeTab === 'generate' && result.html_meta_tags && (
              <>
                <CopyBlock label="HTML Meta Tags (add inside <head>)" text={result.html_meta_tags} />
                <CopyBlock label="JSON-LD LocalBusiness Schema" text={JSON.stringify(result.json_ld_schema, null, 2)} />
                {result.service_areas?.length > 0 && (
                  <p className="text-xs text-slate-500">Service areas: {result.service_areas.join(', ')}</p>
                )}
              </>
            )}

            {/* Local keywords results */}
            {activeTab === 'keywords' && result.keyword_patterns && (
              <>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Keyword Patterns for {result.city}</p>
                  <div className="flex flex-wrap gap-2">
                    {result.keyword_patterns.map((kw, i) => (
                      <span key={i} className="text-xs bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-3 py-1 rounded-full border border-violet-200 dark:border-violet-800">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
                {result.tips?.length > 0 && (
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg space-y-1">
                    {result.tips.map((t, i) => (
                      <p key={i} className="text-xs text-emerald-700 dark:text-emerald-400">✓ {t}</p>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Image guide results */}
            {activeTab === 'image' && result.instructions && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 space-y-2">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Windows</p>
                    {result.instructions.windows.map((s, i) => (
                      <p key={i} className="text-xs text-slate-600 dark:text-slate-400">{i + 1}. {s}</p>
                    ))}
                  </div>
                  <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 space-y-2">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">macOS</p>
                    {result.instructions.mac.map((s, i) => (
                      <p key={i} className="text-xs text-slate-600 dark:text-slate-400">{i + 1}. {s}</p>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">Recommended Tools</p>
                  <div className="flex flex-wrap gap-2">
                    {result.instructions.tools.map((t) => (
                      <a
                        key={t.name}
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-violet-600 hover:underline"
                      >
                        {t.name}
                      </a>
                    ))}
                  </div>
                </div>
                {result.seo_benefits?.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg space-y-1">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">SEO Benefits</p>
                    {result.seo_benefits.map((b, i) => (
                      <p key={i} className="text-xs text-blue-700 dark:text-blue-400">• {b}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
